import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Phone, Mail, MapPin, Clock } from "lucide-react";

export function SiteFooter() {
  const [user, setUser] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [settings, setSettings] = useState<{ contact_phone: string | null; contact_email: string | null } | null>(null);

  useEffect(() => {
    let mounted = true;
    const check = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!mounted) return;
      setUser(user ?? null);
      if (!user) { setIsAdmin(false); return; }
      const { data } = await supabase.from("user_roles").select("role").eq("user_id", user.id).eq("role", "admin").maybeSingle();
      if (mounted) setIsAdmin(!!data);
    };
    const loadSettings = async () => {
      const { data } = await supabase.from("settings").select("contact_phone, contact_email").eq("id", 1).maybeSingle();
      if (mounted) setSettings(data ?? null);
    };
    check();
    loadSettings();
    const { data: sub } = supabase.auth.onAuthStateChange(() => check());
    return () => { mounted = false; sub.subscription.unsubscribe(); };
  }, []);

  return (
    <footer className="mt-20 bg-espresso text-cream">
      <div className="mx-auto grid max-w-7xl gap-10 px-6 py-14 md:grid-cols-4">
        <div>
          <div className="font-display text-2xl text-accent">Creed Car Rental</div>
          <p className="mt-3 text-sm text-cream/70">Boutique luxury car rentals in Miami. Delivered to your door, insurance included.</p>
        </div>
        <div>
          <div className="text-sm font-semibold text-accent">Explore</div>
          <ul className="mt-3 space-y-2 text-sm text-cream/80 [&_a:hover]:text-accent [&_a]:transition-colors">
            <li><Link to="/fleet">Our Fleet</Link></li>
            <li><Link to="/how-it-works">How it works</Link></li>
            <li><Link to="/about">About</Link></li>
            <li><Link to="/faq">FAQ</Link></li>
            <li><Link to="/contact">Contact</Link></li>
            <li><Link to="/review">Leave a review</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-sm font-semibold text-accent">Get in touch</div>
          <ul className="mt-3 space-y-2 text-sm text-cream/80 [&_a:hover]:text-accent [&_a]:transition-colors">
            {settings?.contact_phone && (
              <li className="flex items-center gap-2"><Phone className="h-4 w-4" /> <a href={`tel:${settings.contact_phone}`}>{settings.contact_phone}</a></li>
            )}
            {settings?.contact_email && (
              <li className="flex items-center gap-2"><Mail className="h-4 w-4" /> <a href={`mailto:${settings.contact_email}`}>{settings.contact_email}</a></li>
            )}
            <li className="flex items-center gap-2"><MapPin className="h-4 w-4" /> Miami-Dade &amp; Broward</li>
            <li className="flex items-center gap-2"><Clock className="h-4 w-4" /> Mon–Sun · 8am–10pm ET</li>
            <li>
              <Link
                to="/miami-airport-luxury-car-rental"
                title="Miami Airport luxury car rental & MIA delivery"
              >
                Miami Airport (MIA) luxury rental
              </Link>
            </li>
          </ul>
        </div>
        <div>
          <div className="text-sm font-semibold text-accent">Good to know</div>
          <ul className="mt-3 space-y-2 text-sm text-cream/80 [&_a:hover]:text-accent [&_a]:transition-colors">
            <li>24/7 roadside</li>
            <li>Drivers must be 25+</li>
            <li><Link to="/privacy">Privacy &amp; Trust</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-cream/10 flex items-center justify-between gap-4 px-6 py-5">
        <div className="text-xs text-cream/80">
          © {new Date().getFullYear()} Creed Car Rental. All rights reserved.
        </div>
        <div className="flex items-center gap-4">
          {isAdmin && (
            <Link to="/admin" className="text-xs font-medium text-accent hover:text-accent/80">Admin</Link>
          )}
          {user ? (
            <button onClick={() => supabase.auth.signOut()} className="text-xs font-medium text-cream/85 hover:text-cream">Sign Out</button>
          ) : (
            <Link to="/auth" className="text-xs font-medium text-cream/85 hover:text-cream">Sign In</Link>
          )}
        </div>
      </div>
    </footer>
  );
}
